import { Container, Typography } from '@cleartrip/ct-design-components'
import React, { useLayoutEffect, useRef, useState } from 'react'

import {
    INDIGO_CAPTION_CONFIG,
    INDIGO_UPFRONT_NUDGE_THEME,
    PE_INTRO_TRANSITION_MS,
    PE_NUDGE_BANNER_COPY_NUDGE,
    PE_NUDGE_BG,
    UPGRADE_NUDGE_VARIANT,
    UpgradeNudgeVariant,
} from '@/constants/flights/premiumEconomy'

const PE_NUDGE_ARROW_HEIGHT_PX = 12
const PE_NUDGE_STRIP_HEIGHT_PX = 32

type PENudgeBannerProps = {
    isExiting?: boolean
    onCaretClick?: () => void
    peTabAnchorRef: React.RefObject<HTMLElement | null>
    text?: string
    variant?: UpgradeNudgeVariant
}

const getArrowCss = (leftPx: number, stripBg: string) => ({
    top: `-${PE_NUDGE_ARROW_HEIGHT_PX}px`,
    left: `${leftPx}px`,
    transform: 'translateX(-50%)',
    width: 0,
    height: 0,
    borderLeft: `${PE_NUDGE_ARROW_HEIGHT_PX / 2}px solid transparent`,
    borderRight: `${PE_NUDGE_ARROW_HEIGHT_PX / 2}px solid transparent`,
    borderBottom: `${PE_NUDGE_ARROW_HEIGHT_PX}px solid ${stripBg}`,
})

const PENudgeBanner: React.FC<PENudgeBannerProps> = ({
    isExiting = false,
    onCaretClick,
    peTabAnchorRef,
    text,
    variant = UPGRADE_NUDGE_VARIANT.PREMIUM,
}) => {
    const isIndigo = variant === UPGRADE_NUDGE_VARIANT.INDIGO
    const stripBg = isIndigo ? INDIGO_UPFRONT_NUDGE_THEME.captionBg : PE_NUDGE_BG
    const resolvedText = text ?? (isIndigo ? INDIGO_CAPTION_CONFIG.copy.caption : PE_NUDGE_BANNER_COPY_NUDGE)
    const bannerRef = useRef<HTMLDivElement | null>(null)
    const [arrowLeftPx, setArrowLeftPx] = useState<number | null>(null)

    useLayoutEffect(() => {
        const updateArrowPosition = () => {
            const tabEl = peTabAnchorRef.current
            const bannerEl = bannerRef.current
            if (!tabEl || !bannerEl) return

            const tabRect = tabEl.getBoundingClientRect()
            const bannerRect = bannerEl.getBoundingClientRect()
            setArrowLeftPx(tabRect.left + tabRect.width / 2 - bannerRect.left)
        }

        updateArrowPosition()
        const retryId = window.setTimeout(updateArrowPosition, 100)
        window.addEventListener('resize', updateArrowPosition)

        return () => {
            window.clearTimeout(retryId)
            window.removeEventListener('resize', updateArrowPosition)
        }
    }, [peTabAnchorRef, isExiting])

    if (!resolvedText) return null

    return (
        <Container
            ref={bannerRef}
            className={`p-relative w-100p bg-white ${isExiting ? 'o-hidden' : 'o-visible'}`}
            css={{
                transition: `opacity ${PE_INTRO_TRANSITION_MS}ms ease-out, max-height ${PE_INTRO_TRANSITION_MS}ms ease-out`,
                maxHeight: isExiting ? 0 : `${PE_NUDGE_STRIP_HEIGHT_PX + PE_NUDGE_ARROW_HEIGHT_PX}px`,
                opacity: isExiting ? 0 : 1,
            }}
        >
            {arrowLeftPx != null && (
                <Container className="p-absolute z-1" css={getArrowCss(arrowLeftPx, stripBg)} />
            )}
            <Container
                className="flex flex-middle w-100p px-6 py-2"
                css={{ backgroundColor: stripBg, minHeight: `${PE_NUDGE_STRIP_HEIGHT_PX}px`, cursor: onCaretClick ? 'pointer' : undefined }}
                data-testid="upgrade-caption-strip"
                onClick={onCaretClick}
            >
                <Typography
                    className={isIndigo ? 'fw-500' : undefined}
                    colorCode={isIndigo ? INDIGO_UPFRONT_NUDGE_THEME.captionText : '#1A1A1A'}
                    variant={isIndigo ? 'B3' : 'B2'}
                >
                    {resolvedText}
                </Typography>
            </Container>
        </Container>
    )
}

export default PENudgeBanner
